export interface AreaReview {
  name: string;
  rating: number;
  text: string;
}

export interface AreaDetail {
  slug: string;
  name: string;
  region: string;
  tagline: string;
  summary: string;
  intro: string[];
  landmarks: string[];
  review: AreaReview;
  nearby: string[];
}

export const areaDetails: AreaDetail[] = [
  {
    slug: "nagarabhavi",
    name: "Nagarabhavi",
    region: "West Bangalore",
    tagline: "Local packers and movers in Nagarabhavi",
    summary: "Our home base — the most active area for same-day house and flat shifting in West Bangalore.",
    intro: [
      "Nagarabhavi is where Lahari Packers and Movers started, and it is still the locality we serve the most. Our teams have worked these streets since 2009, so a crew and vehicle can reach most Nagarabhavi addresses quickly. We regularly shift families between Nagarabhavi 1st, 2nd, 3rd and 4th Stage, Vinayaka Nagar, Gnanabharathi and the roads around Bangalore University.",
      "Whether you are moving a 1 BHK flat close to Nagarabhavi Ring Road or a full independent house near Nagarabhavi Metro, we handle packing, loading, moving and re-assembly the same day. Our teams also know the society rules and parking limits along the Outer Ring Road service roads, so loading is quick and your neighbours stay happy.",
    ],
    landmarks: ["Nagarabhavi Ring Road", "Bangalore University", "Jnana Jyothi Nagar", "Nagarabhavi Metro"],
    review: {
      name: "Deepa R.",
      rating: 5,
      text: "Shifted from Nagarabhavi 2nd Stage to a bigger flat nearby. The crew knew the building rules and finished everything in one afternoon — no damage at all.",
    },
    nearby: ["Vijayanagar", "Basaveshwaranagar", "Kengeri"],
  },
  {
    slug: "rajajinagar",
    name: "Rajajinagar",
    region: "West Bangalore",
    tagline: "Trusted local shifting in Rajajinagar",
    summary: "Apartment, independent house and small-shop shifting across Rajajinagar's blocks.",
    intro: [
      "Rajajinagar is one of the busiest residential and commercial pockets of West Bangalore, and our local shifting crews cover every block from 1st Block to 6th Block. We commonly move families from apartments near Rajajinagar Metro, Navrang Circle and Bashyam Circle, along with shops and small offices in Rajajinagar Industrial Town.",
      "Because many Rajajinagar buildings are older, we pay extra attention to staircase turns and narrow doorways. Furniture is measured, wrapped and carried with edge guards so walls and door frames stay untouched, and your goods reach the new address without a scratch.",
    ],
    landmarks: ["Rajajinagar Metro", "Navrang Circle", "ESI Hospital", "Rajajinagar Industrial Town"],
    review: {
      name: "Kiran Kumar",
      rating: 5,
      text: "Moved our 3 BHK in Rajajinagar 5th Block. Packing was neat and the team was on time. Very organised and polite staff.",
    },
    nearby: ["Malleswaram", "Vijayanagar", "Basaveshwaranagar"],
  },
  {
    slug: "malleswaram",
    name: "Malleswaram",
    region: "Central-North Bangalore",
    tagline: "House shifting in Malleswaram with careful handling",
    summary: "Gentle, careful local moving for Malleswaram's heritage homes and modern apartments.",
    intro: [
      "Malleswaram's mix of old heritage homes and newer apartments needs a moving team that is patient with antiques, glass-fronted cupboards and solid wooden furniture. Our local shifting service in Malleswaram is used by families moving around Sampige Road, 8th Cross, 15th Cross and the streets near Sankey Tank.",
      "We pack in layers — bubble wrap, foam and corrugated sheets — then label every carton room by room. Loading is planned around Malleswaram's narrow lanes and one-way stretches, so your move finishes without long waits or awkward double parking.",
    ],
    landmarks: ["Sampige Road", "8th Cross Malleswaram", "Mantri Mall", "Sankey Tank"],
    review: {
      name: "Ananya Bhat",
      rating: 5,
      text: "We have old wooden furniture and they handled it beautifully. Local shifting in Malleswaram was done in a single day.",
    },
    nearby: ["Rajajinagar", "Basaveshwaranagar", "Jayanagar"],
  },
  {
    slug: "vijayanagar",
    name: "Vijayanagar",
    region: "West Bangalore",
    tagline: "Budget local movers in Vijayanagar",
    summary: "Quick, budget-friendly shifting for 1, 2 and 3 BHK homes in Vijayanagar.",
    intro: [
      "Vijayanagar is a favourite for families who want an affordable move without cutting corners on care. Our local shifting team covers Vijayanagar, Chord Road, Maruthi Mandir, Govindraj Nagar and the roads around Vijayanagar Metro, handling everything from a single item pickup to a complete household.",
      "You get a clear, item-wise estimate before the move so there are no surprises on shifting day. If you are moving on a tight budget, we suggest the right packing level to protect your goods without overspending.",
    ],
    landmarks: ["Vijayanagar Metro", "Chord Road", "Maruthi Mandir", "Govindraj Nagar"],
    review: {
      name: "Suresh N.",
      rating: 5,
      text: "Affordable and honest pricing. Shifted my 2 BHK within Vijayanagar in a few hours. Recommended.",
    },
    nearby: ["Nagarabhavi", "Rajajinagar", "Basaveshwaranagar"],
  },
  {
    slug: "basaveshwaranagar",
    name: "Basaveshwaranagar",
    region: "West Bangalore",
    tagline: "Same-city relocation in Basaveshwaranagar",
    summary: "Packing, loading, moving and unpacking in one trip across Basaveshwaranagar.",
    intro: [
      "Basaveshwaranagar's wide, well-planned streets make it easy for us to park close to your building, which keeps loading and unloading fast. We handle local shifting across Basaveshwaranagar 1st, 2nd and 3rd Stage, near Havanur Circle, Shankar Mutt and Nagarabhavi Main Road.",
      "Most moves in this area finish comfortably within a day. We dismantle beds and wardrobes, wrap all fragile items and reassemble furniture at your new home, so you only need to direct us where things go.",
    ],
    landmarks: ["Basaveshwaranagar 3rd Stage", "Havanur Circle", "Shankar Mutt", "Nagarabhavi Main Road"],
    review: {
      name: "Priya Shetty",
      rating: 5,
      text: "Shifted within Basaveshwaranagar 3rd Stage. Packing, moving and setting up the furniture — all done in one day.",
    },
    nearby: ["Nagarabhavi", "Rajajinagar", "Malleswaram"],
  },
  {
    slug: "jayanagar",
    name: "Jayanagar",
    region: "South Bangalore",
    tagline: "Local packers and movers in Jayanagar",
    summary: "Careful same-day shifting for Jayanagar families and gated-community apartments.",
    intro: [
      "Jayanagar is full of tree-lined streets and gated layouts, and our local moving crews know the parking and gate rules here well. We serve every block, from Jayanagar 4th Block and the shopping complex area to South End Circle and the roads around Jayanagar Metro.",
      "Because many families here include senior citizens, we plan the move to be as calm as possible — early starts, gentle handling and clear communication at every step. Everything is packed, shifted and set up so you can settle in the same evening.",
    ],
    landmarks: ["Jayanagar 4th Block", "South End Circle", "Jayanagar Shopping Complex", "Jayanagar Metro"],
    review: {
      name: "Ramesh Gowda",
      rating: 5,
      text: "Moved my parents from Jayanagar 4th Block. The team was patient and respectful throughout. Great service.",
    },
    nearby: ["JP Nagar", "Banashankari", "Koramangala"],
  },
  {
    slug: "jp-nagar",
    name: "JP Nagar",
    region: "South Bangalore",
    tagline: "Flexible local shifting in JP Nagar",
    summary: "Weekend and month-end friendly moving for working families in JP Nagar.",
    intro: [
      "JP Nagar's phases and layouts stretch across a wide area, and we cover them all — JP Nagar 1st to 8th Phase, Sarakki Lake, Brigade Millennium and the streets near JP Nagar Metro. Our local shifting service is popular with working families who prefer weekend or month-end slots.",
      "We confirm your slot in advance and arrive with a full crew and dedicated truck, so the move does not spill into another day. Fragile items, televisions and appliances get their own protection packs for safe transit.",
    ],
    landmarks: ["JP Nagar 6th Phase", "Sarakki Lake", "Brigade Millennium", "JP Nagar Metro"],
    review: {
      name: "Meghana Rao",
      rating: 5,
      text: "Booked a Sunday slot for our JP Nagar move. Everything was packed and delivered the same day. Very convenient.",
    },
    nearby: ["Jayanagar", "Banashankari", "HSR Layout"],
  },
  {
    slug: "banashankari",
    name: "Banashankari",
    region: "South Bangalore",
    tagline: "House shifting in Banashankari",
    summary: "Dedicated trucks and trained crews for South Bangalore households in Banashankari.",
    intro: [
      "Banashankari is one of the largest residential zones in South Bangalore, and we shift families throughout it — Banashankari 1st, 2nd and 3rd Stage, Kathriguppe, BDA Complex and the roads near Banashankari Temple and Outer Ring Road.",
      "From compact 1 BHK flats to large independent houses, we bring the right crew and vehicle for the job. Our packing is done room by room and cross-checked at delivery, so nothing gets left behind or mixed up.",
    ],
    landmarks: ["Banashankari Temple", "Banashankari 2nd Stage", "Kathriguppe", "Outer Ring Road"],
    review: {
      name: "Vasanthi S.",
      rating: 5,
      text: "Shifted our entire house in Banashankari 3rd Stage. Good packing material and zero breakage. Thank you.",
    },
    nearby: ["Jayanagar", "JP Nagar", "Kengeri"],
  },
  {
    slug: "hsr-layout",
    name: "HSR Layout",
    region: "South-East Bangalore",
    tagline: "Local movers in HSR Layout",
    summary: "Fast shifting for IT professionals, bachelors and startup offices in HSR Layout.",
    intro: [
      "HSR Layout is a magnet for young families and tech professionals, and most of our moves here are fast, short-distance shifts between sectors. We cover HSR Layout sectors 1 to 7, the 27th Main and BDA Complex areas, and the streets near Agara Lake.",
      "Bachelors and shared flats usually need a quick partial move, while startups need workstations and chairs shifted carefully. We handle both — packing only what you need, loading efficiently and delivering to your new sector the same day.",
    ],
    landmarks: ["HSR Layout Sector 2", "27th Main HSR", "Agara Lake", "BDA Complex HSR"],
    review: {
      name: "Arjun M.",
      rating: 5,
      text: "Quick and affordable move within HSR sectors. Team was friendly and careful with my electronics.",
    },
    nearby: ["Koramangala", "Marathahalli", "JP Nagar"],
  },
  {
    slug: "koramangala",
    name: "Koramangala",
    region: "South-East Bangalore",
    tagline: "Local shifting in Koramangala",
    summary: "Compact apartments, high-rise lifts and restricted parking handled smoothly in Koramangala.",
    intro: [
      "Koramangala's dense blocks and busy roads mean a local move here needs tight coordination. Our crews shift families across Koramangala 1st to 8th Block, near Forum Mall, Sony World Signal and 80 Feet Road, always working around building and parking restrictions.",
      "High-rise apartments often need lift booking and a fixed loading window. We confirm these in advance, bring the right trolleys and edge guards for corridors, and finish within the allowed time so there are no penalties or delays.",
    ],
    landmarks: ["Koramangala 5th Block", "Forum Mall", "Sony World Signal", "80 Feet Road"],
    review: {
      name: "Nikhil Verma",
      rating: 5,
      text: "Handled our high-rise move in Koramangala 6th Block perfectly. Booking the lift was coordinated in advance.",
    },
    nearby: ["HSR Layout", "Jayanagar", "Marathahalli"],
  },
  {
    slug: "whitefield",
    name: "Whitefield",
    region: "East Bangalore",
    tagline: "Local packers and movers in Whitefield",
    summary: "Villa, apartment and tech-park shifting across Whitefield and nearby layouts.",
    intro: [
      "Whitefield stretches from ITPL and Hoodi to Kadugodi and Varthur, and our local shifting teams cover the whole belt. We handle villa moves, large apartments near Phoenix Mall of Asia and offices inside the tech parks, often combining home and office packing in one plan.",
      "Distances within Whitefield can be long, so we route the truck smartly to avoid peak-hour jams on Whitefield Main Road. Goods are packed securely and delivered without unnecessary time on the road.",
    ],
    landmarks: ["ITPL Whitefield", "Phoenix Mall of Asia", "Hoodi Circle", "Whitefield Main Road"],
    review: {
      name: "Shalini K.",
      rating: 5,
      text: "Villa shifting in Whitefield handled very well. Big team, good packing and delivered safely the same day.",
    },
    nearby: ["Marathahalli", "Yelahanka", "HSR Layout"],
  },
  {
    slug: "marathahalli",
    name: "Marathahalli",
    region: "East Bangalore",
    tagline: "Local shifting in Marathahalli",
    summary: "Quick loading and unloading support for apartments around the Outer Ring Road.",
    intro: [
      "Marathahalli is all about the Outer Ring Road, and our crews know exactly how to move around its traffic. We shift families in Marathahalli, AECS Layout, Munnekolala and the roads near Marathahalli Bridge, timing loading to avoid the worst of the rush.",
      "Most apartments here have strict move-in timings and small service lifts. We plan for both — packing compactly, using trolleys and strap-guarding furniture so the move fits neatly into your building's slot.",
    ],
    landmarks: ["Marathahalli Bridge", "Outer Ring Road", "AECS Layout", "Munnekolala"],
    review: {
      name: "Kavya Reddy",
      rating: 5,
      text: "Smooth local move in Marathahalli. They managed the small service lift without any trouble.",
    },
    nearby: ["Whitefield", "HSR Layout", "Koramangala"],
  },
  {
    slug: "kengeri",
    name: "Kengeri",
    region: "South-West Bangalore",
    tagline: "Local movers in Kengeri and Mysore Road",
    summary: "Affordable same-city house shifting for any budget along the Mysore Road corridor.",
    intro: [
      "Kengeri, Satellite Town and the Mysore Road corridor are among the most affordable parts of Bangalore to move within, and we keep our pricing just as sensible. Our team covers Kengeri, Kengeri Satellite Town, NICE Road and the areas bordering RR Nagar.",
      "Whether you are shifting a small rented flat or a family home, you get the same careful packing and a dedicated vehicle. We also handle short storage if your new home is not ready on the moving date.",
    ],
    landmarks: ["Kengeri Metro", "Kengeri Satellite Town", "Mysore Road", "NICE Road"],
    review: {
      name: "Manjunath P.",
      rating: 5,
      text: "Moved from Kengeri to Mysore Road on a low budget. Good service and no hidden charges.",
    },
    nearby: ["RR Nagar", "Nagarabhavi", "Banashankari"],
  },
  {
    slug: "rr-nagar",
    name: "RR Nagar",
    region: "South-West Bangalore",
    tagline: "Local shifting service in RR Nagar",
    summary: "House, villa and small-office shifting across Rajarajeshwari Nagar.",
    intro: [
      "Rajarajeshwari Nagar, or RR Nagar, is a fast-growing residential belt with villas, plots and gated layouts. We shift families across RR Nagar, Ideal Homes, BEML Layout and Uttarahalli, handling everything from a single room to a complete villa.",
      "Wide roads and newer buildings usually make moves here quick, but we still plan parking and lift access ahead of time. Fragile and heavy items are packed and moved with the right material for a damage-free delivery.",
    ],
    landmarks: ["RR Nagar Arch", "Ideal Homes Township", "BEML Layout", "Uttarahalli"],
    review: {
      name: "Sunitha Reddy",
      rating: 5,
      text: "Shifted our villa items in RR Nagar. Careful packing and the crew was very cooperative.",
    },
    nearby: ["Kengeri", "Banashankari", "Jayanagar"],
  },
  {
    slug: "yelahanka",
    name: "Yelahanka",
    region: "North Bangalore",
    tagline: "Local packers and movers in Yelahanka",
    summary: "North Bangalore apartment, plot and gated-layout shifting in Yelahanka.",
    intro: [
      "Yelahanka and its surrounding layouts have become a popular choice for families moving to North Bangalore. We provide local shifting across Yelahanka New Town, Kogilu, Bagalur Road and the areas near Jakkur, handling apartments, villas and plots alike.",
      "Because many new layouts here are still developing, we confirm road access and parking before the move. Goods are packed carefully, loaded into a closed-body truck and delivered straight to your new address.",
    ],
    landmarks: ["Yelahanka New Town", "Kogilu", "Bagalur Road", "Jakkur"],
    review: {
      name: "Farhan A.",
      rating: 5,
      text: "Local shift in Yelahanka New Town went smoothly. On-time crew and neat work.",
    },
    nearby: ["Whitefield", "Malleswaram", "Rajajinagar"],
  },
  {
    slug: "anikethana-nagara",
    name: "Anikethana Nagara",
    region: "North-West Bangalore",
    tagline: "Packers and movers in Anikethana Nagara, Kannahalli",
    summary: "Our home base — same-day house, flat and office shifting from Anikethana Nagara, Kannahalli.",
    intro: [
      "Anikethana Nagara in Kannahalli is where our operations office now sits, so a crew and vehicle can be at your doorstep faster than almost anywhere in the city. We handle local shifting across Anikethana Nagara, Kannahalli Main Road and the surrounding layouts near Nelamangala Road and Hesarghatta Road.",
      "Because we are based right here, same-day pickups are the norm rather than the exception. Families moving a 1 BHK or 2 BHK flat nearby, or shifting a full independent house, get packing, loading, moving and re-assembly done neatly within a single day.",
    ],
    landmarks: ["Anikethana Nagara", "Kannahalli Main Road", "Nelamangala Road", "Hesarghatta Road"],
    review: {
      name: "Mahesh B.",
      rating: 5,
      text: "Shifted within Anikethana Nagara and the crew was here within half an hour of my call. Quick, careful and very reasonably priced.",
    },
    nearby: ["Kannahalli", "Giddenahalli", "Seegehalli"],
  },
  {
    slug: "kannahalli",
    name: "Kannahalli",
    region: "North-West Bangalore",
    tagline: "Local packers and movers in Kannahalli",
    summary: "Fast local shifting around Kannahalli and the Nelamangala Road belt.",
    intro: [
      "Kannahalli is our operational hub, and our crews move through it every day. We cover local shifting across Kannahalli, Kannahalli Main Road, Anikethana Nagara and the growing layouts towards Nelamangala Road and Hesarghatta Road.",
      "Whether you are shifting a rented flat or a family home, you get the same careful packing and a dedicated vehicle. Short-notice moves are easy here because the warehouse and office are just around the corner.",
    ],
    landmarks: ["Kannahalli Main Road", "Anikethana Nagara", "Nelamangala Road", "Hesarghatta Road"],
    review: {
      name: "Naveen K.",
      rating: 5,
      text: "Booked a local shift in Kannahalli at short notice and they arranged it the same day. Good packing and no damage.",
    },
    nearby: ["Anikethana Nagara", "Seegehalli", "Giddenahalli"],
  },
  {
    slug: "giddenahalli",
    name: "Giddenahalli",
    region: "North-West Bangalore",
    tagline: "Local moving service in Giddenahalli",
    summary: "Careful house and flat shifting across Giddenahalli's growing residential pockets.",
    intro: [
      "Giddenahalli has grown quickly into a busy residential pocket, and our local shifting teams cover it along with the roads towards Kannahalli, Nelamangala Road and Hesarghatta Road. Most of our moves here are within the neighbourhood or a short hop to the areas nearby.",
      "Because some lanes are still developing, we confirm road access and loading space before the move. Everything is then packed, loaded into a closed-body truck and delivered without delay.",
    ],
    landmarks: ["Giddenahalli Cross", "Kannahalli", "Nelamangala Road", "Hesarghatta Road"],
    review: {
      name: "Lakshmi N.",
      rating: 5,
      text: "Moved our furniture within Giddenahalli. The team handled the narrow lane and still finished on time.",
    },
    nearby: ["Kannahalli", "Seegehalli", "Anikethana Nagara"],
  },
  {
    slug: "seegehalli",
    name: "Seegehalli",
    region: "North Bangalore",
    tagline: "Local packers and movers in Seegehalli",
    summary: "New-layout and apartment shifting in and around Seegehalli.",
    intro: [
      "Seegehalli and its newer layouts are a popular choice for families moving to North Bangalore, and our crews cover it along with Kannur, Kogilu Cross and Thanisandra Road. We handle apartments, villas and plot-side homes alike.",
      "Many buildings here are recent, so we plan lift booking and loading windows in advance. Fragile and heavy items are packed with the right material for a damage-free delivery.",
    ],
    landmarks: ["Seegehalli Main Road", "Kannur", "Kogilu Cross", "Thanisandra Road"],
    review: {
      name: "Divya S.",
      rating: 5,
      text: "Shifted into a new apartment in Seegehalli. On-time team, neat packing and a smooth single-day move.",
    },
    nearby: ["Yelahanka", "Kannahalli", "Giddenahalli"],
  },
  {
    slug: "magadi-road",
    name: "Magadi Road",
    region: "West Bangalore",
    tagline: "Local packers and movers on Magadi Road",
    summary: "Affordable house and shop shifting along the busy Magadi Road corridor.",
    intro: [
      "Magadi Road is one of the busiest corridors in West Bangalore, and our local shifting crews cover the stretch from Magadi Road Toll Gate and Prashanth Nagar to Bapuji Nagar and Magadi Road Metro. We move families, rented flats and small shops with the same care.",
      "Loading on a busy road needs planning, so we time arrivals to avoid peak traffic and park close to your building. Goods are packed room by room and cross-checked at delivery, so nothing is missed.",
    ],
    landmarks: ["Magadi Road Toll Gate", "Prashanth Nagar", "Bapuji Nagar", "Magadi Road Metro"],
    review: {
      name: "Rakesh T.",
      rating: 5,
      text: "Shifted our 2 BHK on Magadi Road. They knew the traffic well and finished before the evening rush. Very pleased.",
    },
    nearby: ["RR Nagar", "Kengeri", "Vijayanagar"],
  },
  {
    slug: "nice-road",
    name: "NICE Road",
    region: "South-West Bangalore",
    tagline: "Local movers along NICE Road",
    summary: "Quick shifting for homes and layouts along the NICE Road belt.",
    intro: [
      "The NICE Road belt links West and South Bangalore, and our crews move families all along it, from the Kengeri end to the Mysore Road junction and the layouts bordering RR Nagar. It is a favourite stretch for budget-friendly local moves.",
      "With fast access to the expressway, we reach most addresses here quickly and keep loading times short. You still get the same item-wise estimate and careful packing we are known for.",
    ],
    landmarks: ["NICE Road Toll", "Kengeri", "Mysore Road Junction", "RR Nagar"],
    review: {
      name: "Sneha P.",
      rating: 5,
      text: "Relocated near NICE Road and the truck reached both ends quickly. Good value for money and careful handling.",
    },
    nearby: ["Kengeri", "RR Nagar", "Nagarabhavi"],
  },
];

export const getAreaDetail = (slug: string): AreaDetail | undefined =>
  areaDetails.find((area) => area.slug === slug);

export interface AreaFaq {
  q: string;
  a: string;
}

export const areaFaqs: Record<string, AreaFaq[]> = {
  nagarabhavi: [
    {
      q: "How quickly can you start a local move in Nagarabhavi?",
      a: "Because Nagarabhavi is one of our most active areas, we can usually reach most streets here the same day. Same-day slots depend on crew availability, so calling early gives you more choice.",
    },
    {
      q: "Do you shift between the different stages of Nagarabhavi?",
      a: "Yes. We regularly move families between Nagarabhavi 1st, 2nd, 3rd and 4th Stage, Vinayaka Nagar, Gnanabharathi and the roads around Bangalore University.",
    },
    {
      q: "Is parking an issue for loading in Nagarabhavi?",
      a: "The Outer Ring Road service roads and some layouts have parking limits. Our crew knows these restrictions and plans loading so your move stays quick and your neighbours stay undisturbed.",
    },
  ],
  rajajinagar: [
    {
      q: "Can you manage older buildings in Rajajinagar?",
      a: "Yes. Many Rajajinagar blocks have older staircases and narrow doorways, so we measure furniture, use edge guards and wrap every corner before carrying anything out.",
    },
    {
      q: "Do you move shops and small offices in Rajajinagar?",
      a: "We do. Along with homes, we shift shops and small offices in Rajajinagar Industrial Town, Navrang Circle and Bashyam Circle.",
    },
    {
      q: "How long does a Rajajinagar local move take?",
      a: "Most Rajajinagar household moves are packed, shifted and set up within a single day, provided lift and loading access are available at both ends.",
    },
  ],
  malleswaram: [
    {
      q: "Do you handle antique and wooden furniture in Malleswaram?",
      a: "Yes. Malleswaram's heritage homes often have solid wood and antique pieces, so we pack them in layers using bubble wrap, foam and corrugated sheets before they are moved.",
    },
    {
      q: "Can you work around Malleswaram's narrow one-way lanes?",
      a: "We plan loading and routing around the narrow lanes and one-way stretches, so there are no long waits or awkward double parking on your moving day.",
    },
    {
      q: "Which parts of Malleswaram do you cover?",
      a: "We cover all of Malleswaram, including Sampige Road, 8th Cross, 15th Cross and the streets around Sankey Tank.",
    },
  ],
  vijayanagar: [
    {
      q: "Is Vijayanagar local shifting affordable?",
      a: "Yes. Vijayanagar is a favourite for budget-conscious families, and you get an item-wise estimate upfront so you know the cost well before moving day.",
    },
    {
      q: "Do you offer partial moves in Vijayanagar?",
      a: "We handle everything from a single-item pickup to a complete household, so partial moves within Vijayanagar are no problem at all.",
    },
    {
      q: "Which Vijayanagar areas do you serve?",
      a: "We cover Vijayanagar, Chord Road, Maruthi Mandir, Govindraj Nagar and the roads around Vijayanagar Metro.",
    },
  ],
  basaveshwaranagar: [
    {
      q: "Can a Basaveshwaranagar move finish in one day?",
      a: "Yes. The wide, well-planned streets here make it easy to park close to your building, so most Basaveshwaranagar moves finish comfortably within a single day.",
    },
    {
      q: "Do you reassemble furniture after moving?",
      a: "We dismantle beds and wardrobes before the move and reassemble them at your new Basaveshwaranagar home, so you only need to direct where things go.",
    },
    {
      q: "Which stages of Basaveshwaranagar do you cover?",
      a: "We serve Basaveshwaranagar 1st, 2nd and 3rd Stage, along with Havanur Circle, Shankar Mutt and Nagarabhavi Main Road.",
    },
  ],
  jayanagar: [
    {
      q: "Do you move senior citizens' homes in Jayanagar?",
      a: "Yes. Many Jayanagar families include senior citizens, so we plan early starts, gentle handling and clear communication to keep the whole move calm and stress-free.",
    },
    {
      q: "Do you know Jayanagar's gated layout rules?",
      a: "Our crews know the parking and gate rules across Jayanagar's blocks and gated layouts, so loading is smooth and fully compliant.",
    },
    {
      q: "Which Jayanagar areas do you cover?",
      a: "We serve every block of Jayanagar, from 4th Block and the shopping complex area to South End Circle and the roads around Jayanagar Metro.",
    },
  ],
  "jp-nagar": [
    {
      q: "Do you offer weekend slots in JP Nagar?",
      a: "Yes. JP Nagar moves are popular with working families, so weekend and month-end slots can be booked in advance to suit your schedule.",
    },
    {
      q: "Can you cover all JP Nagar phases?",
      a: "We cover JP Nagar 1st to 8th Phase, Sarakki Lake, Brigade Millennium and the streets near JP Nagar Metro.",
    },
    {
      q: "Will my JP Nagar move finish the same day?",
      a: "We arrive with a full crew and a dedicated truck for your confirmed slot, so the move does not spill into another day.",
    },
  ],
  banashankari: [
    {
      q: "Do you bring the right crew for large Banashankari homes?",
      a: "Yes. From compact 1 BHK flats to large independent houses, we match the crew and vehicle size to your exact requirement.",
    },
    {
      q: "How do you make sure nothing is missed during the move?",
      a: "Packing is done room by room and cross-checked at delivery, so nothing is left behind or mixed up between rooms.",
    },
    {
      q: "Which Banashankari areas do you cover?",
      a: "We cover Banashankari 1st, 2nd and 3rd Stage, Kathriguppe, BDA Complex and the roads near Banashankari Temple and Outer Ring Road.",
    },
  ],
  "hsr-layout": [
    {
      q: "Do you do quick partial moves in HSR Layout?",
      a: "Yes. Bachelors and shared flats often need fast partial moves, so we pack only what you need and deliver to your new sector the same day.",
    },
    {
      q: "Can you relocate startup offices in HSR Layout?",
      a: "We handle workstations, ergonomic chairs and small office setups carefully, so HSR startups can move without long downtime.",
    },
    {
      q: "Which HSR sectors do you cover?",
      a: "We cover HSR Layout sectors 1 to 7, the 27th Main and BDA Complex areas, and the streets near Agara Lake.",
    },
  ],
  koramangala: [
    {
      q: "How do you handle Koramangala high-rises?",
      a: "High-rise apartments often need lift booking and a fixed loading window, so we confirm these in advance and bring trolleys and edge guards for the corridors.",
    },
    {
      q: "Do you manage Koramangala parking restrictions?",
      a: "Yes. Our crews work around building and parking restrictions across Koramangala's dense blocks, finishing within the allowed time so you avoid penalties.",
    },
    {
      q: "Which Koramangala blocks do you cover?",
      a: "We shift families across Koramangala 1st to 8th Block, near Forum Mall, Sony World Signal and 80 Feet Road.",
    },
  ],
  whitefield: [
    {
      q: "Do you handle villa moves in Whitefield?",
      a: "Yes. We handle villa moves, large apartments and offices, and can even combine home and office packing in a single plan.",
    },
    {
      q: "How do you manage Whitefield traffic?",
      a: "Distances within Whitefield can be long, so we route the truck smartly to avoid peak-hour jams on Whitefield Main Road.",
    },
    {
      q: "Which Whitefield areas do you cover?",
      a: "We cover the whole Whitefield belt, from ITPL and Hoodi to Kadugodi, Varthur and the areas near Phoenix Mall of Asia.",
    },
  ],
  marathahalli: [
    {
      q: "Can you work with Marathahalli's move-in timings?",
      a: "Yes. Many Marathahalli apartments have strict move-in windows and small service lifts, so we plan compact packing and trolley use to fit your slot.",
    },
    {
      q: "Do you avoid the Marathahalli traffic rush?",
      a: "We time loading to avoid the worst of the Outer Ring Road rush, so your move stays on schedule and your goods spend less time on the road.",
    },
    {
      q: "Which Marathahalli areas do you cover?",
      a: "We cover Marathahalli, AECS Layout, Munnekolala and the roads near Marathahalli Bridge.",
    },
  ],
  kengeri: [
    {
      q: "Is Kengeri local shifting budget friendly?",
      a: "Yes. Kengeri and the Mysore Road corridor are among the most affordable areas to move within, and our pricing stays just as sensible with no hidden charges.",
    },
    {
      q: "Do you offer short storage near Kengeri?",
      a: "If your new home is not ready on the moving date, we can hold your goods in our secure Bangalore warehouse for short periods.",
    },
    {
      q: "Which Kengeri areas do you cover?",
      a: "We cover Kengeri, Kengeri Satellite Town, NICE Road and the areas bordering RR Nagar.",
    },
  ],
  "rr-nagar": [
    {
      q: "Do you move villas in RR Nagar?",
      a: "Yes. RR Nagar has many villas and gated layouts, and we handle everything from a single room to a complete villa move.",
    },
    {
      q: "Are moves in RR Nagar quick?",
      a: "Wide roads and newer buildings usually make RR Nagar moves quick, though we still plan parking and lift access ahead of time.",
    },
    {
      q: "Which RR Nagar areas do you cover?",
      a: "We cover RR Nagar, Ideal Homes Township, BEML Layout and Uttarahalli.",
    },
  ],
  yelahanka: [
    {
      q: "Do you confirm road access in new Yelahanka layouts?",
      a: "Yes. Many new Yelahanka layouts are still developing, so we confirm road access and parking before the move to avoid any delay.",
    },
    {
      q: "Do you move villas and plot-side homes in Yelahanka?",
      a: "We handle apartments, villas and plot-side homes across Yelahanka and its surrounding layouts.",
    },
    {
      q: "Which Yelahanka areas do you cover?",
      a: "We cover Yelahanka New Town, Kogilu, Bagalur Road and the areas near Jakkur.",
    },
  ],
  "anikethana-nagara": [
    {
      q: "How quickly can you start a move in Anikethana Nagara?",
      a: "Our office is right here in Anikethana Nagara, Kannahalli, so we can often reach you within the hour and arrange same-day shifting when a crew is free.",
    },
    {
      q: "Do you shift between Anikethana Nagara and nearby areas?",
      a: "Yes. We regularly move families between Anikethana Nagara, Kannahalli, Giddenahalli, Seegehalli and the layouts along Nelamangala Road and Hesarghatta Road.",
    },
    {
      q: "Can I visit your office before booking?",
      a: "Of course. Drop by our Anikethana Nagara office during working hours and we will walk you through the packing plan and a clear item-wise estimate.",
    },
  ],
  kannahalli: [
    {
      q: "Is Kannahalli your own base of operations?",
      a: "Yes. Kannahalli is our operational hub with our office and warehouse nearby, so local moves here are quick to arrange, including short-notice ones.",
    },
    {
      q: "Do you offer storage for Kannahalli moves?",
      a: "We do. If your new home is not ready, we can hold your packed goods safely in our warehouse and deliver them whenever you are ready.",
    },
    {
      q: "Which areas near Kannahalli do you cover?",
      a: "We cover Kannahalli, Anikethana Nagara, Giddenahalli, Seegehalli and the roads towards Nelamangala and Hesarghatta.",
    },
  ],
  giddenahalli: [
    {
      q: "Do you manage narrow lanes in Giddenahalli?",
      a: "Yes. Some Giddenahalli lanes are still developing, so we confirm road access and loading space beforehand and choose the right vehicle for the job.",
    },
    {
      q: "Can a Giddenahalli move finish the same day?",
      a: "Most local moves around Giddenahalli are packed, shifted and set up within a single day, depending on access and crew availability.",
    },
    {
      q: "Which nearby areas do you also serve?",
      a: "We also cover Kannahalli, Seegehalli and Anikethana Nagara, along with the Nelamangala Road and Hesarghatta Road belt.",
    },
  ],
  seegehalli: [
    {
      q: "Do you handle moves in new Seegehalli apartments?",
      a: "Yes. Many Seegehalli buildings are recent, so we plan lift booking and loading windows in advance to keep the move smooth and delay-free.",
    },
    {
      q: "Do you move villas and plot-side homes in Seegehalli?",
      a: "We handle apartments, villas and plot-side homes across Seegehalli and the surrounding layouts.",
    },
    {
      q: "Which Seegehalli areas do you cover?",
      a: "We cover Seegehalli Main Road, Kannur, Kogilu Cross and the roads towards Thanisandra Road.",
    },
  ],
  "magadi-road": [
    {
      q: "Can you load on a busy road like Magadi Road?",
      a: "Yes. Magadi Road is one of the busiest corridors in West Bangalore, so we time arrivals to avoid peak traffic and park as close to your building as possible.",
    },
    {
      q: "Do you move shops and small offices on Magadi Road?",
      a: "We do. Along with homes and flats, we shift small shops and offices along the Magadi Road corridor with minimal downtime.",
    },
    {
      q: "Which parts of Magadi Road do you cover?",
      a: "We cover the stretch from Magadi Road Toll Gate and Prashanth Nagar to Bapuji Nagar and the roads near Magadi Road Metro.",
    },
  ],
  "nice-road": [
    {
      q: "Why are NICE Road moves quick?",
      a: "Fast access to the NICE Road expressway lets us reach most addresses along the belt quickly and keep loading times short, so your day is not spent waiting.",
    },
    {
      q: "Do you serve both ends of the NICE Road belt?",
      a: "Yes. We cover the belt from the Kengeri end to the Mysore Road junction and the layouts bordering RR Nagar and Nagarabhavi.",
    },
    {
      q: "Is NICE Road shifting affordable?",
      a: "The corridor is popular for budget-friendly local moves. You still get a transparent, item-wise estimate before the move with no hidden charges.",
    },
  ],
};

export const getAreaFaqs = (slug: string): AreaFaq[] => areaFaqs[slug] ?? [];